import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Phone, MapPin, Clock, Check, X,
  RotateCcw, Loader2, CalendarDays, Plus, LogOut
} from "lucide-react";
import { Button } from "./components/ui/button";
import { API, formatDate, DAY_NAMES, MONTH_NAMES, statusColors } from "./components/crmtech/utils";
import { TechLogin } from "./components/crmtech/TechLogin";
import { AppointmentDetail } from "./components/crmtech/AppointmentDetail";
import { NewClientForm } from "./components/crmtech/NewClientForm";

const CrmTech = () => {
  const [techToken, setTechToken] = useState(localStorage.getItem("tech_token") || "");
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState("day");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("tech_token");
      if (!token) { setAuthChecked(true); return; }
      try {
        await axios.get(`${API}/tech/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setTechToken(token);
      } catch {
        localStorage.removeItem("tech_token");
        localStorage.removeItem("tech_username");
        setTechToken("");
      }
      setAuthChecked(true);
    };
    checkAuth();
  }, []);

  const fetchAppointments = useCallback(async () => {
    if (!techToken) return;
    setLoading(true);
    try {
      let startDate, endDate;
      if (view === "day") {
        startDate = endDate = formatDate(currentDate);
      } else if (view === "week") {
        const day = currentDate.getDay();
        const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(currentDate);
        monday.setDate(diff);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        startDate = formatDate(monday);
        endDate = formatDate(sunday);
      } else {
        const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const last = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        startDate = formatDate(first);
        endDate = formatDate(last);
      }
      const res = await axios.get(`${API}/appointments?start_date=${startDate}&end_date=${endDate}`, {
        headers: techToken ? { Authorization: `Bearer ${techToken}` } : {}
      });
      setAppointments(res.data);
    } catch {
      toast.error("Error al cargar citas");
    } finally {
      setLoading(false);
    }
  }, [currentDate, view, techToken]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const handleLogout = () => {
    const token = localStorage.getItem("tech_token");
    if (token) axios.post(`${API}/tech/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem("tech_token");
    localStorage.removeItem("tech_username");
    setTechToken("");
    setCurrentDate(new Date()); // reset to today on logout
    setSelectedAppointment(null);
    setAppointments([]);
  };

  const navigateDate = (dir) => {
    const d = new Date(currentDate);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const handleCancelAppointment = async (apptId, undo = false) => {
    try {
      const newStatus = undo ? "Instalación programada" : "Anulado";
      await axios.put(`${API}/appointments/${apptId}`, { status: newStatus }, {
        headers: techToken ? { Authorization: `Bearer ${techToken}` } : {}
      });
      toast.success(undo ? "Instalación restaurada" : "Instalación anulada");
      setCancelConfirm(null);
      fetchAppointments();
    } catch {
      toast.error("Error");
    }
  };

  if (!authChecked) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!techToken) return <TechLogin onLogin={(token) => setTechToken(token)} />;

  if (selectedAppointment) {
    return <AppointmentDetail appointment={selectedAppointment} onBack={() => { setSelectedAppointment(null); fetchAppointments(); }} onUpdate={fetchAppointments} />;
  }

  if (showNewClient) {
    return <NewClientForm currentDate={currentDate} onBack={() => setShowNewClient(false)} onCreated={() => { setShowNewClient(false); fetchAppointments(); }} />;
  }

  const dayLabel = `${DAY_NAMES[currentDate.getDay()]} ${currentDate.getDate()} ${MONTH_NAMES[currentDate.getMonth()]}`;

  const getWeekDays = () => {
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(currentDate);
    monday.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  };

  const AppointmentCard = ({ appt }) => {
    const isDone = appt.status !== "Instalación programada" && (appt.tech_validated || appt.status === "Instalado");
    const isCancelled = appt.status === "Anulado";
    const isBonus = appt.created_by_tech === true;
    const longPressRef = useRef(null);

    const handleTouchStart = () => {
      longPressRef.current = setTimeout(() => {
        if (isDone) return;
        if (isCancelled) setCancelConfirm({ id: appt.id, undo: true });
        else setCancelConfirm({ id: appt.id, undo: false });
      }, 600);
    };
    const handleTouchEnd = () => clearTimeout(longPressRef.current);

    return (
      <button
        onClick={() => setSelectedAppointment(appt)}
        onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchCancel={handleTouchEnd}
        onMouseDown={handleTouchStart} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd}
        className={`w-full text-left p-3 rounded-xl border shadow-sm hover:shadow-md transition-all active:scale-[0.98] ${isDone ? "bg-emerald-50 border-emerald-200" : isCancelled ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}
        data-testid={`appt-card-${appt.id}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-1 h-8 rounded-full" style={{ backgroundColor: isDone ? "#22c55e" : (appt.color || "#3b82f6") }} />
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">
                  {isDone && <Check size={14} className="inline mr-1 text-emerald-600" />}
                  {appt.client_name} {appt.client_prenom}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock size={11} /> {appt.start_time}</p>
              </div>
            </div>
            {(appt.city || appt.postal_code) && (
              <p className="text-xs text-slate-600 font-medium mt-1.5 flex items-center gap-1 pl-3">
                <MapPin size={11} className="shrink-0" /> {appt.postal_code && `${appt.postal_code} `}{appt.city || ""}
              </p>
            )}
            {appt.address && <p className="text-xs text-slate-500 flex items-center gap-1 truncate pl-6">{appt.address}</p>}
            {appt.phone && (
              <a href={`tel:${appt.phone}`} onClick={e => e.stopPropagation()} className="text-xs text-blue-600 mt-1 flex items-center gap-1 pl-3 hover:text-blue-800">
                <Phone size={11} className="shrink-0" /> {appt.phone}
              </a>
            )}
            {appt.vendeur && <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-1 pl-3">👤 {appt.vendeur}</p>}
            {appt.notes && <p className="text-xs text-amber-700 bg-amber-50 mt-1.5 px-2 py-1 rounded ml-3">&#x26A0;&#xFE0F; {appt.notes}</p>}
          </div>
          <div className="flex flex-col items-end gap-1">
            {isBonus && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-red-50 text-red-600 border border-red-200 whitespace-nowrap" data-testid="bonus-badge">Bonus</span>}
            {isDone ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap flex items-center gap-1"><Check size={10} /> Completado</span>
            ) : isCancelled ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 whitespace-nowrap flex items-center gap-1"><X size={10} /> Anulado</span>
            ) : (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[appt.status] || "bg-slate-100 text-slate-600"}`}>{appt.status || "—"}</span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="tech-app">
      <div className="bg-slate-800 text-white px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button onClick={handleLogout} className="p-1.5 bg-red-600/80 rounded-lg hover:bg-red-700 transition-colors" data-testid="tech-logout-btn" title="Cerrar sesión"><LogOut size={16} /></button>
            <span className="text-xs text-slate-400">{localStorage.getItem("tech_username") || ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNewClient(true)} className="p-1.5 bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors" data-testid="new-client-btn"><Plus size={18} /></button>
            <div className="flex bg-slate-700 rounded-lg p-0.5">
              <button onClick={() => setView("day")} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "day" ? "bg-white text-slate-800" : "text-slate-300"}`} data-testid="view-day">Día</button>
              <button onClick={() => setView("week")} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "week" ? "bg-white text-slate-800" : "text-slate-300"}`} data-testid="view-week">Semana</button>
              <button onClick={() => setView("month")} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${view === "month" ? "bg-white text-slate-800" : "text-slate-300"}`} data-testid="view-month">Mes</button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <button onClick={() => navigateDate(-1)} className="p-1" data-testid="nav-prev"><ChevronLeft size={20} /></button>
          <button onClick={goToday} className="text-sm font-medium" data-testid="nav-today">
            {view === "day" ? dayLabel : view === "week" ? `Semana ${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}` : `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          </button>
          <button onClick={() => navigateDate(1)} className="p-1" data-testid="nav-next"><ChevronRight size={20} /></button>
        </div>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : view === "day" ? (
          <div className="space-y-3">
            {appointments.filter(a => a.date === formatDate(currentDate)).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).map(appt => <AppointmentCard key={appt.id} appt={appt} />)}
            {appointments.filter(a => a.date === formatDate(currentDate)).length === 0 && (
              <div className="text-center py-16 text-slate-400"><CalendarDays size={40} className="mx-auto mb-3 opacity-50" /><p className="text-sm">No hay citas para hoy</p></div>
            )}
          </div>
        ) : view === "week" ? (
          <div className="space-y-4">
            {getWeekDays().map(day => {
              const key = formatDate(day);
              const dayAppts = appointments.filter(a => a.date === key);
              const isToday = key === formatDate(new Date());
              return (
                <div key={key}>
                  <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${isToday ? "text-blue-600" : "text-slate-500"}`}>
                    {DAY_NAMES[day.getDay()]} {day.getDate()}/{day.getMonth()+1}
                    {isToday && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full normal-case">Hoy</span>}
                    <span className="ml-2 text-slate-400 font-normal">({dayAppts.length})</span>
                  </div>
                  <div className="space-y-2 mb-3">
                    {dayAppts.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).map(appt => <AppointmentCard key={appt.id} appt={appt} />)}
                    {dayAppts.length === 0 && <p className="text-xs text-slate-300 pl-2 italic">Sin citas</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          (() => {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const firstDay = new Date(year, month, 1);
            const lastDay = new Date(year, month + 1, 0);
            const startOffset = (firstDay.getDay() + 6) % 7;
            const totalDays = lastDay.getDate();
            const today = formatDate(new Date());
            const cells = [];
            for (let i = 0; i < startOffset; i++) cells.push(null);
            for (let d = 1; d <= totalDays; d++) cells.push(d);
            return (
              <div>
                <div className="grid grid-cols-7 gap-px mb-1">
                  {["L","M","X","J","V","S","D"].map(d => <div key={d} className="text-center text-[10px] font-bold text-slate-400 py-1">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-px">
                  {cells.map((day, i) => {
                    if (!day) return <div key={i} />;
                    const dateKey = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayAppts = appointments.filter(a => a.date === dateKey);
                    const isToday = dateKey === today;
                    return (
                      <button key={i} onClick={() => { setCurrentDate(new Date(year, month, day)); setView("day"); }}
                        className={`p-1 min-h-[52px] rounded-lg text-left flex flex-col items-center transition-colors ${isToday ? "bg-blue-50 border border-blue-300" : "bg-white border border-slate-100 hover:bg-slate-50"}`}
                        data-testid={`month-day-${day}`}>
                        <span className={`text-xs font-semibold ${isToday ? "text-blue-600" : "text-slate-700"}`}>{day}</span>
                        {dayAppts.length > 0 && <span className="mt-0.5 text-[9px] font-bold bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center">{dayAppts.length}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 space-y-2">
                  {appointments.sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)).map(appt => (
                    <button key={appt.id} onClick={() => setSelectedAppointment(appt)}
                      className="w-full text-left p-2.5 bg-white rounded-lg border border-slate-200 flex items-center gap-3"
                      data-testid={`month-appt-${appt.id}`}>
                      <div className="text-center shrink-0 w-10">
                        <div className="text-xs font-bold text-blue-600">{appt.date?.split("-")[2]}</div>
                        <div className="text-[10px] text-slate-400">{appt.start_time}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{appt.client_name} {appt.client_prenom}</p>
                        {appt.address && <p className="text-[10px] text-slate-500 truncate">{appt.address}</p>}
                      </div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${statusColors[appt.status] || "bg-slate-100 text-slate-600"}`}>{appt.status?.split(" ")[0] || "—"}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()
        )}
      </div>

      {cancelConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center" onClick={() => setCancelConfirm(null)} data-testid="cancel-overlay">
          <div className="bg-white w-full max-w-md rounded-t-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 text-center">{cancelConfirm.undo ? "Restaurar instalación" : "Anular instalación"}</h3>
            <p className="text-sm text-slate-500 text-center">{cancelConfirm.undo ? "¿Quieres restaurar esta instalación?" : "¿Estás seguro de que quieres anular esta instalación?"}</p>
            <Button onClick={() => handleCancelAppointment(cancelConfirm.id, cancelConfirm.undo)}
              className={`w-full h-12 text-base font-semibold ${cancelConfirm.undo ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"}`}
              data-testid="confirm-cancel-btn">
              {cancelConfirm.undo ? <><RotateCcw size={18} className="mr-2" /> Restaurar</> : <><X size={18} className="mr-2" /> Confirmar anulación</>}
            </Button>
            <Button variant="outline" onClick={() => setCancelConfirm(null)} className="w-full h-12 text-base" data-testid="dismiss-cancel-btn">Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmTech;
